import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Key, Plus, Copy, Check, Trash2, RefreshCw, Shield, Eye, EyeOff, BookOpen, ChevronDown, ChevronUp, Lock } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERMISSION_LABELS = {
  seo_analysis: 'Analisi SEO',
  meta_tags: 'Meta Tags',
  content_generation: 'Generazione Contenuti',
  local_seo: 'Local SEO'
};

const APIKeysManager = () => {
  const { token } = useAuth();
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyRevealed, setNewKeyRevealed] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [showDocs, setShowDocs] = useState(false);
  const [docs, setDocs] = useState(null);
  const [noAccess, setNoAccess] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  const fetchKeys = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/apikeys`, authHeaders);
      setKeys(res.data);
      setNoAccess(false);
    } catch (err) {
      if (err.response?.status === 403) setNoAccess(true);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchKeys(); }, [fetchKeys]);

  const createKey = async () => {
    if (!newKeyName.trim()) return;
    setCreating(true);
    try {
      const res = await axios.post(`${API_URL}/api/apikeys`, {
        name: newKeyName.trim(),
        permissions: Object.keys(PERMISSION_LABELS)
      }, authHeaders);
      setNewKeyRevealed(res.data.full_key);
      setNewKeyName('');
      fetchKeys();
    } catch (err) {
      alert(err.response?.data?.detail || 'Errore nella creazione');
    } finally {
      setCreating(false);
    }
  };

  const revokeKey = async (id) => {
    if (!window.confirm('Sei sicuro di voler revocare questa API Key?')) return;
    try {
      await axios.delete(`${API_URL}/api/apikeys/${id}`, authHeaders);
      fetchKeys();
    } catch {}
  };

  const regenerateKey = async (id) => {
    if (!window.confirm('Rigenerare la key? La vecchia smetterà di funzionare.')) return;
    try {
      const res = await axios.post(`${API_URL}/api/apikeys/${id}/regenerate`, {}, authHeaders);
      setNewKeyRevealed(res.data.full_key);
      fetchKeys();
    } catch {}
  };

  const copyKey = async (key) => {
    try {
      await navigator.clipboard.writeText(key);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = key;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      ta.remove();
    }
    setCopiedId(key);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const fetchDocs = async () => {
    if (docs) { setShowDocs(!showDocs); return; }
    try {
      const res = await axios.get(`${API_URL}/api/apikeys/docs/reference`, authHeaders);
      setDocs(res.data);
      setShowDocs(true);
    } catch {}
  };

  if (noAccess) return (
    <div className="text-center py-12" data-testid="api-keys-no-access">
      <Lock className="w-12 h-12 text-[#A1A1AA]/20 mx-auto mb-3" />
      <p className="text-sm text-[#A1A1AA]">Disponibile dal piano Team</p>
      <p className="text-xs text-[#A1A1AA]/60 mt-1">Effettua l'upgrade per accedere alle API Keys</p>
    </div>
  );
  if (loading) return null;

  return (
    <div className="space-y-6" data-testid="api-keys-manager">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#6366F1]/10 flex items-center justify-center">
            <Key className="w-5 h-5 text-[#6366F1]" />
          </div>
          <div>
            <h2 className="font-['Outfit'] text-lg font-medium">API Keys</h2>
            <p className="text-xs text-[#A1A1AA]">Integra AEO Orchestra nei tuoi tool</p>
          </div>
        </div>
        <button onClick={fetchDocs} className="text-xs text-[#0055FF] hover:underline flex items-center gap-1" data-testid="api-docs-btn">
          <BookOpen className="w-3 h-3" /> Documentazione API
        </button>
      </div>

      {/* New Key Revealed */}
      {newKeyRevealed && (
        <div className="bg-[#10B981]/10 border border-[#10B981]/30 rounded-xl p-4 space-y-2" data-testid="new-key-revealed">
          <p className="text-sm text-[#10B981] font-medium">Nuova API Key creata!</p>
          <p className="text-[10px] text-[#A1A1AA]">Copiala ora. Non sarà visibile di nuovo.</p>
          <div className="flex items-center gap-2 bg-[#0a0a0a] rounded-lg p-3">
            <code className="text-xs font-mono text-[#00E5FF] flex-1 break-all">{newKeyRevealed}</code>
            <button onClick={() => copyKey(newKeyRevealed)} className="flex-shrink-0">
              {copiedId === newKeyRevealed ? <Check className="w-4 h-4 text-[#10B981]" /> : <Copy className="w-4 h-4 text-[#A1A1AA]" />}
            </button>
          </div>
          <button onClick={() => setNewKeyRevealed(null)} className="text-xs text-[#A1A1AA] hover:text-white">Chiudi</button>
        </div>
      )}

      {/* Create Key Form */}
      <div className="flex gap-2">
        <Input
          value={newKeyName}
          onChange={(e) => setNewKeyName(e.target.value)}
          placeholder="Nome API Key (es: MioCRM, SitoAgenzia)"
          className="bg-[#0a0a0a] border-white/10 text-white flex-1 text-sm"
          onKeyDown={(e) => e.key === 'Enter' && createKey()}
          data-testid="api-key-name-input"
        />
        <Button onClick={createKey} disabled={creating || !newKeyName.trim()} className="bg-[#6366F1] hover:bg-[#5558E3] text-white" data-testid="create-api-key-btn">
          <Plus className="w-4 h-4 mr-1" /> {creating ? '...' : 'Crea'}
        </Button>
      </div>

      {/* Keys List */}
      {keys.length === 0 ? (
        <div className="text-center py-8">
          <Shield className="w-12 h-12 text-[#A1A1AA]/20 mx-auto mb-3" />
          <p className="text-sm text-[#A1A1AA]">Nessuna API Key creata</p>
          <p className="text-xs text-[#A1A1AA]/60">Crea la tua prima key per iniziare ad usare le API</p>
        </div>
      ) : (
        <div className="space-y-3">
          {keys.map((key) => (
            <div key={key.id} className={`bg-[#0a0a0a] rounded-xl p-4 border ${key.status === 'active' ? 'border-white/5' : 'border-red-500/20 opacity-60'}`} data-testid={`api-key-${key.id}`}>
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Key className="w-4 h-4 text-[#6366F1]" />
                  <span className="font-medium text-sm">{key.name}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] ${key.status === 'active' ? 'bg-[#10B981]/20 text-[#10B981]' : 'bg-[#EF4444]/20 text-[#EF4444]'}`}>
                    {key.status === 'active' ? 'Attiva' : 'Revocata'}
                  </span>
                </div>
                {key.status === 'active' && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => regenerateKey(key.id)} className="p-1.5 rounded-lg hover:bg-white/5 text-[#A1A1AA] hover:text-white" title="Rigenera">
                      <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => revokeKey(key.id)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-[#A1A1AA] hover:text-[#EF4444]" title="Revoca">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 mb-2">
                <code className="text-xs font-mono text-[#A1A1AA]">{key.key_preview}</code>
                <button onClick={() => copyKey(key.key_preview)} className="text-[#A1A1AA]">
                  <Copy className="w-3 h-3" />
                </button>
              </div>
              <div className="flex flex-wrap gap-3 text-[10px] text-[#A1A1AA]">
                <span>Chiamate: <strong className="text-white">{key.calls_used}/{key.calls_limit}</strong></span>
                <span>Rate: <strong className="text-white">{key.rate_limit_per_min}/min</strong></span>
                <span>Permessi: {key.permissions?.map(p => PERMISSION_LABELS[p] || p).join(', ')}</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* API Docs */}
      {showDocs && docs && (
        <div className="bg-[#0a0a0a] rounded-xl p-5 border border-white/5 space-y-4" data-testid="api-docs">
          <div className="flex items-center justify-between">
            <h3 className="font-['Outfit'] font-medium text-sm">{docs.title}</h3>
            <button onClick={() => setShowDocs(false)} className="text-[#A1A1AA]"><ChevronUp className="w-4 h-4" /></button>
          </div>
          <div className="bg-[#111111] rounded-lg p-3">
            <p className="text-[10px] text-[#A1A1AA] mb-1">Autenticazione:</p>
            <code className="text-xs text-[#00E5FF]">{docs.authentication.header}: {docs.authentication.example?.split(': ')[1]}</code>
          </div>
          <div className="space-y-3">
            {docs.endpoints?.map((ep, i) => (
              <div key={i} className="border-l-2 border-[#6366F1]/30 pl-3">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#0055FF]/20 text-[#0055FF] font-mono">{ep.method}</span>
                  <code className="text-xs text-white font-mono">{ep.path}</code>
                </div>
                <p className="text-[10px] text-[#A1A1AA]">{ep.description}</p>
              </div>
            ))}
          </div>
          <div className="text-[10px] text-[#A1A1AA]">
            <strong>Rate limits:</strong> B2B: {docs.rate_limits?.b2b_custom} | Pro: {docs.rate_limits?.professional}
          </div>
        </div>
      )}
    </div>
  );
};

export default APIKeysManager;
